// PDF Evidence Pack generator (client-side, jsPDF). Zero external dependency.
import jsPDF from "jspdf";
import { ORG } from "./constants";
import { genId } from "./crypto";

const DISCLAIMER =
  "This evidence package documents locally generated cryptographic and analytical evidence. External certification, notarization, blockchain anchoring, government verification, or third-party timestamping is only represented when independently performed and recorded.";

export function buildPackModel({ records, integrity, sigStatus }) {
  const list = Array.isArray(records) ? records : [records];
  const packId = genId("PACK");
  const created_at = new Date().toISOString();
  const totalFailures = (integrity || []).filter((r) => r && !(r.selfOk && r.linkOk)).length;
  const chainStatus = list.length === 0 ? "EMPTY" : totalFailures > 0 ? "BROKEN" : "INTACT";
  return { packId, created_at, list, chainStatus, integrity, sigStatus };
}

function recordIntegrityLabel(ig) {
  if (!ig) return "N/A";
  if (ig.selfOk && ig.linkOk) return "VERIFIED";
  return ig.selfOk ? "BROKEN LINK" : "TAMPERED";
}

// Writes one record's section into the PDF using the provided line/gap writers.
function writeRecordSection({ line, gap }, records, r, idx, integrity, sigStatus) {
  const globalIdx = Array.isArray(records) ? records.indexOf(r) : idx;
  const integ = recordIntegrityLabel(integrity && integrity[globalIdx]);
  const sig = (sigStatus && sigStatus[r.id]) || "UNSIGNED RECEIPT";
  const sim = r.source_tag === "simulated";

  line(`Record #${globalIdx >= 0 ? globalIdx : idx} — ${r.payload_type}`, { bold: true, size: 10, color: [15, 23, 42] });
  line(`event_id: ${r.event_id}`);
  line(`timestamp: ${r.timestamp}`);
  line(`hash_mode: ${(r.hash_mode || "canonical") === "raw" ? "RAW TEXT" : "CANONICAL JSON"}`);
  line(`sha256: ${r.sha256}`);
  line(`previous_hash: ${r.previous_hash}`);
  line(`integrity: ${integ}`);
  line(`signature: ${sig}`);
  if (r.signature) {
    line(`  algorithm: ${r.signature.algorithm} · key_id: ${r.signature.public_key_id} · signed_at: ${r.signature.signed_at}`);
  }
  line(`drift_status: ${r.drift_status || "NONE"}`);
  if (r.telemetry) {
    const t = r.telemetry;
    line(
      `HCT-3 telemetry: raw_drift=${t.raw_drift ?? "—"} corrected_offset=${t.corrected_offset ?? "—"} refractive_index_v=${t.refractive_index ?? t.refractive_index_v ?? "—"} anomaly_score=${t.anomaly_score ?? "—"}`
    );
  }
  if (r.meta && r.meta.anomaly) line(`anomaly: ${r.meta.anomaly.classification} (score ${r.meta.anomaly.score})`);
  if (r.meta && r.meta.ada) line(`ADA-4WM: assessed at Layer ${r.layer} · ${r.meta.ada.summary || "mirrors mapped"}`);
  line(`verification_method: ${r.verification_method || "Local SHA-256 (WebCrypto)"}`);
  line(`evidence source: ${sim ? "SIMULATED / DEMO TELEMETRY — NOT EXTERNAL SENSOR DATA" : "LOCAL CRYPTOGRAPHIC EVIDENCE"}`, {
    color: sim ? [180, 83, 9] : [15, 118, 110],
  });
  if (r.notes) line(`operator notes: ${r.notes}`);
  gap(6);
}

export function generateEvidencePack({ records, integrity, sigStatus, scopeLabel }) {
  const model = buildPackModel({ records, integrity, sigStatus });
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const M = 40;
  const W = doc.internal.pageSize.getWidth();
  let y = M;

  const line = (txt, opts = {}) => {
    const size = opts.size || 9;
    const color = opts.color || [30, 41, 59];
    doc.setFontSize(size);
    doc.setTextColor(color[0], color[1], color[2]);
    doc.setFont("helvetica", opts.bold ? "bold" : "normal");
    const wrapped = doc.splitTextToSize(String(txt), opts.width || W - M * 2);
    wrapped.forEach((w) => {
      if (y > doc.internal.pageSize.getHeight() - M) {
        doc.addPage();
        y = M;
      }
      doc.text(w, opts.x || M, y);
      y += size + 4;
    });
  };
  const gap = (h = 8) => {
    y += h;
  };
  const rule = () => {
    if (y > doc.internal.pageSize.getHeight() - M) {
      doc.addPage();
      y = M;
    }
    doc.setDrawColor(203, 213, 225);
    doc.line(M, y, W - M, y);
    y += 12;
  };

  // Header band
  doc.setFillColor(7, 10, 15);
  doc.rect(0, 0, W, 70, "F");
  doc.setTextColor(0, 229, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("SignalLink AI — Evidence Pack", M, 34);
  doc.setTextColor(148, 163, 184);
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.text(`${ORG.name}  ·  CAGE ${ORG.cage}  ·  ${ORG.architecture}`, M, 52);
  y = 92;

  // System metadata
  line("SYSTEM METADATA", { bold: true, size: 11, color: [8, 145, 178] });
  gap(2);
  [
    ["Organization", ORG.name],
    ["CAGE", ORG.cage],
    ["System", ORG.system],
    ["Framework", ORG.framework],
    ["Architecture", ORG.architecture],
    ["Cryptographic Engine", "K-Crypto / SHA-256"],
    ["Analytics Engine", "HCT-3"],
    ["Evidence Pack ID", model.packId],
    ["Created", model.created_at],
    ["Scope", scopeLabel || (model.list.length > 1 ? "Full evidence chain" : "Single record")],
    ["Records in pack", String(model.list.length)],
    ["Chain status", model.chainStatus],
  ].forEach(([k, v]) => line(`${k}: ${v}`));
  gap(6);

  // Disclaimer box
  rule();
  line("VERIFICATION STATEMENT", { bold: true, size: 11, color: [8, 145, 178] });
  gap(2);
  line(DISCLAIMER, { color: [71, 85, 105] });
  rule();

  // Records
  line("EVIDENCE RECORDS", { bold: true, size: 11, color: [8, 145, 178] });
  gap(4);

  model.list.forEach((r, idx) => {
    writeRecordSection({ line, gap }, records, r, idx, model.integrity, model.sigStatus);
    if (idx < model.list.length - 1) rule();
  });

  // Reconstruction summary
  rule();
  line("RECONSTRUCTION SUMMARY", { bold: true, size: 11, color: [8, 145, 178] });
  gap(2);
  const firstBreak = (model.integrity || []).findIndex((r) => r && !(r.selfOk && r.linkOk));
  line(
    model.chainStatus === "INTACT"
      ? "Chain continuity intact across all records. Replay/reconstruction available."
      : `Chain continuity breaks at record #${firstBreak}. Records after this point are unverifiable.`,
    { color: [71, 85, 105] }
  );

  // Footer on each page
  const pages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.text(
      `SignalLink AI · Evidence Pack ${model.packId} · Page ${p}/${pages} · Local cryptographic evidence`,
      M,
      doc.internal.pageSize.getHeight() - 18
    );
  }

  doc.save(`SignalLink-EvidencePack-${model.packId}.pdf`);
  return model.packId;
}
